import { useState } from "react";
import { Check, X, MapPin, DollarSign } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

/**
 * WaypointConfirmCard — driver-side one-tap confirm card for a passenger's
 * mid-ride stop request. Shows the new stop address + the new total fare;
 * "Accept" bumps agreed_fare on the ride, "Decline" pops the most-recent
 * stop and reverts the route.
 *
 * Props:
 *  • pending: { ride_id, waypoint:{address,lat,lng}, new_total, surcharge,
 *               distance_km }
 *  • onResolve(): callback after either accept or decline succeeds
 */
export default function WaypointConfirmCard({ pending, onResolve }) {
  const [busy, setBusy] = useState(false);

  const respond = async (accept) => {
    setBusy(true);
    try {
      await api.post(`/chauffeur/rides/${pending.ride_id}/waypoint/confirm`, {
        accept,
      });
      toast.success(accept ? `Accepted — fare updated to JMD ${pending.new_total}` : "Stop declined");
      onResolve?.();
    } catch (err) {
      toast.error(errorMessage(err, "Could not respond"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="waypoint-confirm-card"
      className="p-4 border border-amber-400/40 bg-amber-500/10 rounded-md space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-amber-300">Passenger added a stop</p>
        <span className="text-[10px] text-amber-200/70">+JMD {pending.surcharge || 250}</span>
      </div>
      <div className="flex items-center gap-2 text-sm text-white">
        <MapPin size={14} className="text-purple-300 flex-shrink-0" />
        <span className="truncate">{pending.waypoint?.address || "New stop"}</span>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-white/60">New total · {pending.distance_km} km</span>
        <span className="text-gold font-display text-lg flex items-center gap-1"><DollarSign size={14}/>{pending.new_total}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="waypoint-confirm-accept" onClick={() => respond(true)} disabled={busy}
          className="inline-flex items-center justify-center gap-1 py-2.5 text-xs font-body uppercase tracking-wider bg-emerald-500 text-luxury-black hover:bg-emerald-400 disabled:opacity-50 rounded">
          <Check size={14} /> Accept
        </button>
        <button data-testid="waypoint-confirm-decline" onClick={() => respond(false)} disabled={busy}
          className="inline-flex items-center justify-center gap-1 py-2.5 text-xs font-body uppercase tracking-wider bg-rose-500/20 text-rose-300 border border-rose-500/40 hover:bg-rose-500/30 disabled:opacity-50 rounded">
          <X size={14} /> Decline
        </button>
      </div>
    </div>
  );
}
